import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const API = window.__RUNTIME_CONFIG__?.apiBase || window.location.origin;

export default function PopiaConsentConfirm() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [talentPoolOptIn, setTalentPoolOptIn] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/popia-consent/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else setInfo(d);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not connect to the server. Please try again later.");
        setLoading(false);
      });
  }, [token]);

  const handleConfirm = async () => {
    if (!noticeAccepted) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/popia-consent/${token}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ talent_pool_opt_in: talentPoolOptIn }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not record your consent");
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    background: "#f0f4f8",
    padding: "30px 16px",
    fontFamily: "system-ui, sans-serif",
    display: "flex",
    justifyContent: "center",
  };
  const cardStyle = {
    background: "white",
    borderRadius: 16,
    padding: 32,
    maxWidth: 640,
    width: "100%",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    height: "fit-content",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}><p style={{ textAlign: "center", color: "#6b7280" }}>Loading…</p></div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>❌</div>
            <h2 style={{ color: "#991b1b" }}>Link Unavailable</h2>
            <p style={{ color: "#6b7280" }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted || info?.already_consented) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <h2 style={{ color: "#166534" }}>{submitted ? "Consent Recorded!" : "Already Confirmed"}</h2>
            <p style={{ color: "#6b7280" }}>
              {submitted
                ? "Thank you — your POPIA consent has been recorded on your Talent Pool profile."
                : "You've already confirmed your POPIA consent. No further action is needed."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0 }}>Privacy notice (POPIA)</h1>
        <p style={{ color: "#6b7280" }}>Hi {info?.candidate_name},</p>

        <div style={{ margin: "16px 0", padding: 16, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.7 }}>
            Guud Vision collects your personal information only to process your job application. If your application is unsuccessful, we will keep your information for 12 months after the position is filled, then securely delete it. You may ask us to access, correct or delete your information at any time by emailing <a href="mailto:info@guudvision.health">info@guudvision.health</a>.
          </p>
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#374151", cursor: "pointer", marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={noticeAccepted}
            onChange={(e) => setNoticeAccepted(e.target.checked)}
            style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
          />
          <span>I have read and accept this Privacy Notice.</span>
        </label>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#374151", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={talentPoolOptIn}
            onChange={(e) => setTalentPoolOptIn(e.target.checked)}
            style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
          />
          <span>I agree that Guud Vision may keep my CV and details for up to an additional 12 months (24 months in total) and contact me about future job opportunities. I can withdraw this consent at any time.</span>
        </label>

        {error && <p style={{ color: "#991b1b", fontSize: 14, marginTop: 10 }}>{error}</p>}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || !noticeAccepted}
          title={!noticeAccepted ? "Please accept the Privacy Notice first" : ""}
          style={{
            marginTop: 20, width: "100%", padding: "14px 20px", borderRadius: 8, border: "none",
            background: submitting || !noticeAccepted ? "#9ca3af" : "#166534",
            color: "white", fontWeight: 700, fontSize: 16,
            cursor: submitting || !noticeAccepted ? "default" : "pointer",
          }}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </div>
  );
}
